const OWNER_KEY = (pollId: string): string => `wf:owner:${pollId}`;
const RESP_KEY = (pollId: string): string => `wf:resp:${pollId}`;
const DRAFT_KEY = (pollId: string): string => `wf:draft:${pollId}`;

export function saveOwnerToken(pollId: string, token: string): void {
  try {
    localStorage.setItem(OWNER_KEY(pollId), token);
  } catch {
    /* localStorage may be unavailable (private mode) */
  }
}

export function getOwnerToken(pollId: string): string | null {
  try {
    return localStorage.getItem(OWNER_KEY(pollId));
  } catch {
    return null;
  }
}

export function clearOwnerToken(pollId: string): void {
  try {
    localStorage.removeItem(OWNER_KEY(pollId));
  } catch {
    /* noop */
  }
}

export interface RespondentRecord {
  id: string;
  token: string;
}

export function saveRespondent(pollId: string, record: RespondentRecord): void {
  try {
    localStorage.setItem(RESP_KEY(pollId), JSON.stringify(record));
  } catch {
    /* noop */
  }
}

export function getRespondent(pollId: string): RespondentRecord | null {
  try {
    const raw = localStorage.getItem(RESP_KEY(pollId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RespondentRecord>;
    if (typeof parsed?.id !== 'string' || typeof parsed?.token !== 'string') return null;
    return { id: parsed.id, token: parsed.token };
  } catch {
    return null;
  }
}

export function clearRespondent(pollId: string): void {
  try {
    localStorage.removeItem(RESP_KEY(pollId));
  } catch {
    /* noop */
  }
}

export interface DraftRecord {
  name: string;
  yes: string[];
  maybe: string[];
}

export function saveDraft(pollId: string, draft: DraftRecord): void {
  try {
    if (draft.name.trim() === '' && draft.yes.length === 0 && draft.maybe.length === 0) {
      localStorage.removeItem(DRAFT_KEY(pollId));
      return;
    }
    localStorage.setItem(DRAFT_KEY(pollId), JSON.stringify(draft));
  } catch {
    /* noop */
  }
}

interface LegacyDraft {
  name?: unknown;
  days?: unknown;
  yes?: unknown;
  maybe?: unknown;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((d): d is string => typeof d === 'string');
}

export function getDraft(pollId: string): DraftRecord | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY(pollId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LegacyDraft;
    if (typeof parsed?.name !== 'string') return null;
    // Back-compat: older drafts stored `days` (yes-only).
    const yes = parsed.yes !== undefined ? asStringArray(parsed.yes) : asStringArray(parsed.days);
    const maybe = asStringArray(parsed.maybe);
    return { name: parsed.name, yes, maybe };
  } catch {
    return null;
  }
}

export function clearDraft(pollId: string): void {
  try {
    localStorage.removeItem(DRAFT_KEY(pollId));
  } catch {
    /* noop */
  }
}
